import type { User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore/lite';
import { dbQuestionBank } from '../firebase/client';
import type { UserRole } from './roleTypes';
import { parseRole } from './roleTypes';

/**
 * Firestore collection id (within DB `vehiwheelqa`) holding per-user roles.
 * Override via `REACT_APP_ADMIN_ROLES_COLLECTION`.
 */
export function getAdminRolesCollectionId(): string {
    return process.env.REACT_APP_ADMIN_ROLES_COLLECTION ?? 'admin_user_roles';
}

/**
 * Resolve role from (in order):
 * 1. Dev-only env override (`REACT_APP_DEV_ROLE_OVERRIDE`) when `NODE_ENV === 'development'`.
 * 2. Custom claims on the ID token: `role` or `adminRole`.
 * 3. Firestore doc `{collection}/{uid}` field `role`.
 *
 * Returns `null` if no valid role is found — caller should treat as access denied for this admin app.
 */
export async function loadUserRole(user: User): Promise<UserRole | null> {
    if (process.env.NODE_ENV === 'development') {
        const override = process.env.REACT_APP_DEV_ROLE_OVERRIDE?.trim();
        if (override) {
            const r = parseRole(override);
            if (r) return r;
        }
    }

    const token = await user.getIdTokenResult();
    const claimRaw = token.claims.role ?? token.claims.adminRole;
    if (typeof claimRaw === 'string') {
        const r = parseRole(claimRaw);
        if (r) return r;
    }

    const coll = getAdminRolesCollectionId();
    const ref = doc(dbQuestionBank, coll, user.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;

    const roleField = snap.data()?.role;
    return parseRole(roleField);
}
